import dayjs from "dayjs";
import uniqueId from "lodash/uniqueId";
import { useTranslation } from "react-i18next";
import { usGenerativeChatStore } from "../store/generativeChatStore";
import { langflowChatStream } from "../api/langflowStreamApi";

const useChatStreamSend = (
    orgId: number,
    viewId: number,
    viewValue: string,
    assetId: number,
    getDownloadHref: (fileId: number) => string,
) => {
    const { t } = useTranslation();
    const {
        selectedConversation,
        setSelectedConversation,
        pushChatHistory,
        pushConversation,
        patchConversationMessage,
        setIsTyping,
    } = usGenerativeChatStore();

    const sendMessage = async (chatId: string, prompt: string) => {
        const aiMessageId = uniqueId("ai-conversation-");
        const aiSender = {
            id: "ai",
            name: "Chat AI",
            avatarImageUrl: "/img/thumbs/ai.jpg",
        };

        // Only the loading bubble shows until the first chunk arrives - the AI
        // message is created lazily so we never render an empty bubble. It is
        // pushed with fresh:false so ChatCustomContent renders chunks directly
        // (the network stream is the reveal); the AIWriter typewriter would
        // otherwise reset on every chunk. streaming:true hides the copy action
        // until the response completes.
        let started = false;

        try {
            const result = await langflowChatStream(
                orgId,
                viewId,
                viewValue,
                assetId,
                prompt,
                (chunk) => {
                    if (!started) {
                        started = true;
                        setIsTyping(false);
                        pushConversation(chatId, {
                            id: aiMessageId,
                            sender: aiSender,
                            content: chunk,
                            contextFiles: [],
                            timestamp: dayjs().toDate(),
                            type: "regular",
                            isMyMessage: false,
                            fresh: false,
                            streaming: true,
                        });
                        return;
                    }
                    // get the current accumulated content from the store and append
                    const state = usGenerativeChatStore.getState();
                    const chat = state.chatHistory.find((c) => c.id === chatId);
                    const existing = chat?.conversation?.find((m) => m.id === aiMessageId)?.content ?? "";
                    patchConversationMessage(chatId, aiMessageId, { content: existing + chunk });
                },
                getDownloadHref,
            );

            // Replace the raw streamed tokens with the processed response
            // (markdown download-link rewriting + reference files) and reveal
            // the copy action.
            if (started) {
                patchConversationMessage(chatId, aiMessageId, {
                    content: result.response,
                    contextFiles: result.contextFiles,
                    streaming: false,
                });
            } else {
                pushConversation(chatId, {
                    id: aiMessageId,
                    sender: aiSender,
                    content: result.response,
                    contextFiles: result.contextFiles,
                    timestamp: dayjs().toDate(),
                    type: "regular",
                    isMyMessage: false,
                    fresh: false,
                    streaming: false,
                });
            }
        } catch (e) {
            // The real error (including Langflow flow errors surfaced as an SSE
            // "error" event) is logged; the user sees a generic friendly message.
            console.error("Langflow stream error", e);
            const errorText = t("There was an error in retrieving the response.");
            if (started) {
                // replace any partial streamed text with the error message
                patchConversationMessage(chatId, aiMessageId, {
                    content: errorText,
                    contextFiles: [],
                    streaming: false,
                    error: true,
                });
            } else {
                pushConversation(chatId, {
                    id: aiMessageId,
                    sender: aiSender,
                    content: errorText,
                    contextFiles: [],
                    timestamp: dayjs().toDate(),
                    type: "regular",
                    isMyMessage: false,
                    fresh: false,
                    streaming: false,
                    error: true,
                });
            }
        } finally {
            setIsTyping(false);
        }
    };

    const createConversation = async (chatId: string, prompt: string) => {
        setSelectedConversation(chatId);
        await sendMessage(chatId, prompt);
    };

    const handleSend = async (prompt: string) => {

        setIsTyping(true);

        const myMessageId = uniqueId("ai-conversation-");
        const pushMyMessage = (chatId: string) =>
            pushConversation(chatId, {
                id: myMessageId,
                sender: {
                    id: "1",
                    name: "Me",
                    avatarImageUrl: "/img/avatars/thumb-1.jpg",
                },
                contextFiles: [],
                content: prompt,
                timestamp: dayjs().toDate(),
                type: "regular",
                isMyMessage: true,
            });

        if (selectedConversation) {
            pushMyMessage(selectedConversation);
            await sendMessage(selectedConversation, prompt);
        } else {
            const newId = uniqueId("ai-chat-");
            pushChatHistory({
                id: newId,
                title: prompt,
                lastConversation: "",
                createdTime: dayjs().unix(),
                updatedTime: dayjs().unix(),
                enable: false,
            });
            pushMyMessage(newId);
            await createConversation(newId, prompt);
        }
    };

    return { handleSend };
};

export default useChatStreamSend;
